import React, { useState, useEffect } from 'react';
import { FolderOpen, Send, FileAudio, MessageSquare, X, Loader2, Check } from 'lucide-react';
import { getProjects, exportToProject } from '../../services/speechApi';
import { toast } from '../ui/Toast';

const ExportToProject = ({ generation, onClose, onExported }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [success, setSuccess] = useState(null);

  const canExportFile = generation && generation.output_url && (generation.generation_type === 'tts' || generation.generation_type === 'sts');
  const canExportChat = generation && (generation.output_text || generation.input_text);
  const exportType = canExportFile ? 'file' : 'chat';

  useEffect(() => {
    getProjects()
      .then(p => { setProjects(Array.isArray(p) ? p : []); })
      .catch(() => toast.error('Erreur chargement projets'))
      .finally(() => setLoading(false));
  }, []);

  const handleExport = async () => {
    if (!selectedProject) return toast.warning('Sélectionnez un projet');
    setExporting(true);
    try {
      await exportToProject(selectedProject, generation.id, exportType);
      setSuccess(true);
      toast.success(exportType === 'file' ? 'Audio envoyé au projet' : 'Texte envoyé au chat du projet');
      setTimeout(() => { onExported?.(); onClose(); }, 1200);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '90vw', maxWidth: '460px',
        background: 'var(--card-bg)',
        border: '1px solid rgba(59,130,246,0.2)',
        borderRadius: '20px', padding: '0', overflow: 'hidden',
        boxShadow: '0 0 40px rgba(29,78,216,0.15), 0 0 80px rgba(29,78,216,0.05)',
      }}>

        {/* Header néon */}
        <div style={{
          padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'linear-gradient(135deg, rgba(29,78,216,0.08), rgba(139,92,246,0.06))',
          borderBottom: '1px solid rgba(59,130,246,0.12)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: 36, height: 36, borderRadius: '10px',
              background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 12px rgba(59,130,246,0.15)',
            }}>
              {exportType === 'file' ? <FileAudio size={16} style={{ color: '#3b82f6' }} /> : <MessageSquare size={16} style={{ color: '#3b82f6' }} />}
            </div>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>
                Envoyer vers un projet
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                {exportType === 'file' ? 'L\'audio sera ajouté aux fichiers du projet' : 'Le texte sera envoyé dans le chat du projet'}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: '4px',
          }}>
            <X size={18} />
          </button>
        </div>

        {/* Corps */}
        <div style={{ padding: '20px 24px' }}>

          {/* Type badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '5px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 600,
            background: exportType === 'file' ? 'rgba(139,92,246,0.1)' : 'rgba(59,130,246,0.1)',
            color: exportType === 'file' ? '#a78bfa' : '#60a5fa',
            border: `1px solid ${exportType === 'file' ? 'rgba(139,92,246,0.2)' : 'rgba(59,130,246,0.2)'}`,
            marginBottom: '16px',
          }}>
            {exportType === 'file' ? <FileAudio size={12} /> : <MessageSquare size={12} />}
            {generation.generation_type.toUpperCase()} — {generation.provider} / {generation.model}
          </div>

          {/* Liste projets */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : (
            <div style={{ maxHeight: '280px', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {projects.filter(p => p.project_status !== 'archived').map(p => {
                const isSelected = selectedProject === p.id;
                return (
                  <button key={p.id} onClick={() => setSelectedProject(p.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '12px 14px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                      background: isSelected
                        ? 'linear-gradient(135deg, rgba(29,78,216,0.12), rgba(59,130,246,0.08))'
                        : 'transparent',
                      border: isSelected
                        ? '1px solid rgba(59,130,246,0.3)'
                        : '1px solid transparent',
                      transition: 'all 0.2s', width: '100%', textAlign: 'left',
                      boxShadow: isSelected ? '0 0 12px rgba(59,130,246,0.08)' : 'none',
                    }}
                    onMouseEnter={e => { if (!isSelected) { e.currentTarget.style.background = 'var(--sidebar-active-bg)'; e.currentTarget.style.border = '1px solid var(--card-border)'; }}}
                    onMouseLeave={e => { if (!isSelected) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.border = '1px solid transparent'; }}}
                  >
                    <div style={{
                      width: 32, height: 32, borderRadius: '8px', flexShrink: 0,
                      background: isSelected ? 'rgba(59,130,246,0.15)' : 'var(--input-bg)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.2s',
                    }}>
                      {isSelected
                        ? <Check size={14} style={{ color: '#3b82f6' }} />
                        : <FolderOpen size={14} style={{ color: 'var(--text-secondary)' }} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '13px', fontWeight: isSelected ? 600 : 500,
                        color: isSelected ? '#3b82f6' : 'var(--text)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{p.name}</div>
                      <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>
                        {p.conversation_count || 0} conv. — {p.files_count || 0} fichiers
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid var(--card-border)',
          display: 'flex', gap: '10px', justifyContent: 'flex-end',
        }}>
          <button onClick={onClose} style={{
            padding: '10px 18px', borderRadius: '10px', border: '1px solid var(--card-border)',
            background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '13px',
          }}>Annuler</button>

          <button onClick={handleExport} disabled={!selectedProject || exporting || success}
            style={{
              padding: '10px 22px', borderRadius: '10px', border: 'none', fontSize: '13px',
              cursor: !selectedProject || exporting ? 'not-allowed' : 'pointer',
              background: success
                ? 'linear-gradient(135deg, #059669, #10b981)'
                : !selectedProject || exporting
                  ? 'var(--card-border)'
                  : 'linear-gradient(135deg, #1D4ED8, #2563eb)',
              color: '#fff', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: '8px',
              boxShadow: success
                ? '0 0 16px rgba(16,185,129,0.3)'
                : selectedProject && !exporting
                  ? '0 0 16px rgba(29,78,216,0.3)'
                  : 'none',
              transition: 'all 0.3s',
            }}>
            {success ? (
              <><Check size={16} /> Envoyé</>
            ) : exporting ? (
              <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Envoi...</>
            ) : (
              <><Send size={16} /> Envoyer</>
            )}
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ExportToProject;
